/*
 * Hawkstetler motion — Lenis smooth scroll + GSAP reveals.
 * Quiet by design: short travel, slow ease-outs, everything plays once.
 *
 * Runs only when <head> added `hk-anim` (motion allowed, not the theme editor).
 * Lenis: smooth wheel on desktop, native touch scrolling on phones.
 */
(() => {
  const root = document.documentElement;
  const reveal = () => root.classList.remove('hk-anim', 'hk-intro');
  const intro = root.classList.contains('hk-intro');
  // Already on screen when the page opens: shown as is, never hidden and replayed.
  const onScreen = (el) => el.getBoundingClientRect().top < window.innerHeight;
  const below = (el) => !onScreen(el);

  if (!root.classList.contains('hk-anim') || !window.gsap || !window.ScrollTrigger) {
    reveal();
    return;
  }
  window.hkMotionReady = true;

  const { gsap, ScrollTrigger, SplitText } = window;
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  const EASE = 'power3.out';
  const touch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const once = (trigger, start = 'top 88%') => ({ trigger, start, once: true });

  /* ------------------------------------------------------------ smooth scroll */
  let lenis = null;
  if (window.Lenis) {
    lenis = new window.Lenis({ lerp: 0.09, smoothWheel: true, syncTouch: false });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((time) => lenis.raf(time * 1000));
    gsap.ticker.lagSmoothing(0);
    window.hkLenis = lenis;
  }

  // In-page links glide (and land below the fixed header).
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href');
    if (id.length < 2) return;
    const target = document.getElementById(id.slice(1));
    if (!target) return;
    event.preventDefault();
    const header = document.querySelector('[data-hk-header]');
    const offset = header ? -header.offsetHeight : 0;
    if (lenis) lenis.scrollTo(target, { offset, duration: 1.4 });
    else window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY + offset, behavior: 'smooth' });
  });

  /* ------------------------------------------------------------ reveals */
  // Inside these, the parent animates as one piece (cards, rows) or nothing does (menu, header).
  const SKIP = [
    '.hk-menu', '.hk-header', '.hk-hero', '.hk-cmodal', '.hk-glight', '.hk-gallery__grid',
    '.hk-card', '.hk-series__card', '.hk-archive__row', '.hk-available__art', '.hk-step',
    '.hk-release__facts', '.hk-chero__facts', '.hk-artist__stats', '.hk-product__facts',
    '.hk-commissions__steps', '.hk-faq__list', '.hk-footer__cols', '.hk-footer__bottom',
    '.hk-studio__facts', '.hk-studio__stats', '.hk-about__panel',
  ].join(',');
  const free = (el) => !el.parentElement || !el.parentElement.closest(SKIP);

  function header() {
    if (!intro) return;
    const bar = document.querySelector('[data-hk-header] .hk-header__bar');
    if (bar) gsap.fromTo(bar, { autoAlpha: 0, y: -14 }, { autoAlpha: 1, y: 0, duration: 1, ease: EASE, delay: 0.1 });
  }

  function hero() {
    const section = document.querySelector('.hk-hero');
    if (!section || !intro) return;
    const tl = gsap.timeline({ defaults: { ease: EASE } });
    const img = section.querySelector('.hk-hero__media img');
    if (img) tl.fromTo(img, { scale: 1.08 }, { scale: 1, duration: 2.4, ease: 'power2.out' }, 0);
    const lead = $$('.hk-hero__copy > :not(h1)', section).filter((el) => el.offsetParent !== null);
    const [first, ...rest] = lead;
    if (first) tl.fromTo(first, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.3);
    if (rest.length) tl.fromTo(rest, { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.75);
    const side = section.querySelector('.hk-hero__side');
    if (side) tl.fromTo(side, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 1 }, 0.85);
  }

  // Headlines: each word rises out of its line mask.
  function titles() {
    const heroTitle = document.querySelector('.hk-hero h1');
    $$('.hk-display').forEach((el) => {
      if (el.closest('.hk-menu')) return;
      if (!intro && onScreen(el)) return; // later pages: titles on screen are simply there
      const isHero = el === heroTitle || onScreen(el);
      if (!SplitText) {
        gsap.fromTo(el, { autoAlpha: 0, y: 24 }, {
          autoAlpha: 1, y: 0, duration: 1.1, ease: EASE, delay: isHero ? 0.35 : 0,
          scrollTrigger: isHero ? undefined : once(el, 'top 90%'),
        });
        return;
      }
      SplitText.create(el, {
        type: 'lines,words',
        mask: 'lines',
        linesClass: 'hk-line-mask',
        autoSplit: true,
        onSplit(self) {
          gsap.set(el, { visibility: 'visible' });
          return gsap.from(self.words, {
            yPercent: 110,
            duration: 1.15,
            stagger: 0.06,
            ease: EASE,
            delay: isHero ? 0.35 : 0,
            scrollTrigger: isHero ? undefined : once(el, 'top 90%'),
          });
        },
      });
    });
  }

  // Eyebrows, copy, links and button rows: a short fade-up, batched so neighbours stagger.
  function text() {
    const els = $$('.hk-label, .hk-copy, .hk-small, .hk-btns, .hk-link, .hk-chero__crumb, .hk-product__crumb, .hk-swipe__foot, .hk-form')
      .filter(free).filter(below);
    // drop anything nested inside another element we're already animating
    const picked = new Set(els);
    const tops = els.filter((el) => {
      for (let p = el.parentElement; p; p = p.parentElement) if (picked.has(p)) return false;
      return true;
    });
    els.length = 0;
    els.push(...tops);
    if (!els.length) return;
    gsap.set(els, { autoAlpha: 0, y: 16 });
    ScrollTrigger.batch(els, {
      start: 'top 92%',
      once: true,
      onEnter: (batch) => gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08, ease: EASE, overwrite: true }),
    });
  }

  // Artwork / series / archive cards: rise in a stagger while the image settles from a slight zoom.
  function cards() {
    const els = $$('.hk-card, .hk-series__card, .hk-archive__row, .hk-available__art, .hk-step').filter(
      (el) => !el.closest('.hk-menu') && below(el)
    );
    if (!els.length) return;
    gsap.set(els, { autoAlpha: 0, y: 40 });
    const imgs = els.map((el) => el.querySelector('img')).filter(Boolean);
    gsap.set(imgs, { scale: 1.08 });
    ScrollTrigger.batch(els, {
      start: 'top 94%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1.1, stagger: 0.1, ease: EASE, clearProps: 'transform' });
        const inner = batch.map((el) => el.querySelector('img')).filter(Boolean);
        // clear afterwards so the CSS hover zoom keeps working
        gsap.to(inner, { scale: 1, duration: 1.6, stagger: 0.1, ease: 'power2.out', clearProps: 'transform' });
      },
    });
  }

  // Rows of facts, stats, steps, FAQ lines and footer columns: one after another.
  function rows() {
    ['.hk-release__facts', '.hk-chero__facts', '.hk-artist__stats', '.hk-product__facts',
      '.hk-commissions__steps', '.hk-faq__list', '.hk-footer__cols', '.hk-footer__bottom',
      '.hk-studio__facts', '.hk-studio__stats'].forEach((sel) => {
      $$(sel).filter(below).forEach((group) => {
        const items = Array.from(group.children);
        if (!items.length) return;
        gsap.fromTo(items, { autoAlpha: 0, y: 14 }, {
          autoAlpha: 1, y: 0, duration: 0.9, stagger: 0.08, ease: EASE, scrollTrigger: once(group, 'top 92%'),
        });
      });
    });
  }

  // Large images: a clean wipe up with the picture settling inside it.
  function media() {
    $$('.hk-artist__portrait, .hk-chero__figure .hk-media, .hk-product__image--first').filter(below).forEach((el) => {
      const img = el.querySelector('img');
      const tl = gsap.timeline({ scrollTrigger: once(el, 'top 85%') });
      tl.fromTo(el, { clipPath: 'inset(100% 0% 0% 0%)' }, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.4, ease: 'power4.inOut' });
      if (img) tl.fromTo(img, { scale: 1.15 }, { scale: 1, duration: 1.8, ease: 'power2.out', clearProps: 'transform' }, 0);
    });
    $$('.hk-artist__inset').filter(below).forEach((el) => {
      gsap.fromTo(el, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 1.1, ease: EASE, delay: 0.5, scrollTrigger: once(el, 'top 95%') });
    });
    $$('.hk-commissions__panel, .hk-commissions__inset, .hk-hero__card, .hk-about__panel').filter(below).forEach((el) => {
      if (el.closest('.hk-hero')) return;
      gsap.fromTo(el, { autoAlpha: 0, y: 30 }, { autoAlpha: 1, y: 0, duration: 1.1, ease: EASE, scrollTrigger: once(el, 'top 88%') });
    });
    $$('.hk-footer__logo').filter(below).forEach((el) => {
      gsap.fromTo(el, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 1.3, ease: EASE, scrollTrigger: once(el, 'top 95%') });
    });
  }

  // A very light parallax on the two full-bleed room photos (desktop only).
  function parallax() {
    if (touch) return;
    $$('.hk-commissions__media img, .hk-collector__media img, .hk-about__media img').forEach((img) => {
      const section = img.closest('section') || img;
      gsap.set(img, { scale: 1.12 });
      gsap.fromTo(img, { yPercent: -4 }, {
        yPercent: 4, ease: 'none',
        scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    });
  }

  // Everything below the fold is set up at once (it is off screen, so hiding it never shows).
  header();
  hero();
  text();
  cards();
  rows();
  media();
  parallax();
  window.addEventListener('load', () => ScrollTrigger.refresh());

  // Headlines wait for the Didot so lines split where they'll actually break (at most 1.2s;
  // only the opening page keeps its titles hidden meanwhile).
  const fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
  Promise.race([fonts, new Promise((r) => setTimeout(r, 1200))]).then(() => {
    titles();
    reveal(); // anything left hidden by CSS is now owned by GSAP
  });
})();
